import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import supabaseClient from '@/api/supabaseClient';
import { RealtimeChannel } from '@supabase/supabase-js';

// Timeout for Realtime subscription (10 minutes - extraction with GPT-4o can be slow)
const REALTIME_TIMEOUT_MS = 600000;

export type PaperCode = '1EN0/01' | '1EN0/02' | '1ET0/01' | '1ET0/02';

export interface UploadPastPaperParams {
  file: File;
  subject: 'english_language' | 'english_literature';
  examBoard: 'edexcel';
  paperNumber: 'Paper 1' | 'Paper 2';
  month: string;
  year: string;
}

export interface UploadPastPaperResult {
  pastPaperId: string;
  filename: string;
}

/**
 * Generate the file path for a past paper PDF
 * Format: {examBoard}/{subject}/{paperCode}/{year}-{month}.pdf
 * Example: edexcel/english-language/1EN0-01/2024-june.pdf
 */
const generateFilePath = (params: Omit<UploadPastPaperParams, 'file'>): string => {
  const subject = params.subject === 'english_language' ? 'english-language' : 'english-literature';
  const paperCode = getPaperCode(params.subject, params.paperNumber).replace('/', '-');
  const month = params.month.toLowerCase();

  return `${params.examBoard}/${subject}/${paperCode}/${params.year}-${month}.pdf`;
};

/**
 * Get the paper code based on subject and paper number
 */
export const getPaperCode = (
  subject: 'english_language' | 'english_literature',
  paperNumber: 'Paper 1' | 'Paper 2'
): PaperCode => {
  if (subject === 'english_language') {
    return paperNumber === 'Paper 1' ? '1EN0/01' : '1EN0/02';
  } else {
    return paperNumber === 'Paper 1' ? '1ET0/01' : '1ET0/02';
  }
};

interface EdgeFunctionResponse {
  data?: { pastPaperId: string };
  errors?: string[];
}

/**
 * Call edge function - completion is detected via Realtime subscription
 * Only reject on 4xx validation errors; ignore 5xx timeouts (function keeps running)
 */
const invokeEdgeFunction = async (
  functionName: string,
  body: Record<string, unknown>,
  onValidationError: (error: Error) => void
): Promise<void> => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const { data: sessionData } = await supabaseClient.auth.getSession();
  const accessToken = sessionData?.session?.access_token;

  if (!accessToken) {
    throw new Error('Not authenticated');
  }

  // Fire and forget - don't await! We rely on Realtime subscription for completion.
  // Using .then()/.catch() instead of await so the function returns immediately.
  fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  }).then(async (response) => {
    if (!response.ok) {
      // Only fail on 4xx (validation errors) - 5xx means function is still running
      if (response.status >= 400 && response.status < 500) {
        try {
          const data: EdgeFunctionResponse = await response.json();
          const errorMessage = data?.errors?.join(', ') || 'Validation failed';
          console.error('Validation error:', errorMessage);
          onValidationError(new Error(errorMessage));
        } catch {
          onValidationError(new Error('Failed to process PDF'));
        }
      } else {
        console.log(`Gateway timeout (${response.status}) - function still running, waiting for Realtime...`);
      }
    }
  }).catch((error) => {
    console.log('Fetch error (function may still be running):', error);
  });
};

interface RealtimeWaitResult {
  promise: Promise<string>;
  rejectWithError: (error: Error) => void;
  cleanup: () => void;
}

/**
 * Set up Realtime subscription for past paper creation
 * Returns a promise that resolves when the past paper is created,
 * plus a function to reject it early if the edge function fails
 */
const setupRealtimeWait = (filePath: string, timeoutMs: number): RealtimeWaitResult => {
  let channel: RealtimeChannel | null = null;
  let timeoutId: NodeJS.Timeout | null = null;
  let rejectFn: ((error: Error) => void) | null = null;

  const cleanup = () => {
    if (timeoutId) clearTimeout(timeoutId);
    if (channel) {
      supabaseClient.removeChannel(channel);
      channel = null;
    }
  };

  const promise = new Promise<string>((resolve, reject) => {
    rejectFn = reject;

    // Set timeout
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('Extraction is taking longer than expected. Please check the past papers list in a few minutes.'));
    }, timeoutMs);

    // Subscribe to INSERT events on past_papers table
    // Note: Don't use filter with paths containing '/' - check payload instead
    channel = supabaseClient
      .channel(`past-paper-creation-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'past_papers',
        },
        (payload) => {
          // Check if this is our paper (filter didn't work with paths containing '/')
          const newPaper = payload.new as { id: string; pdf_url: string };
          console.log('Realtime INSERT received:', newPaper.pdf_url);
          if (newPaper.pdf_url === filePath) {
            console.log('✅ Realtime: Past paper created!', payload);
            cleanup();
            resolve(newPaper.id);
          }
        }
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
        if (status === 'CHANNEL_ERROR') {
          cleanup();
          reject(new Error('Failed to subscribe to updates. Please try again.'));
        }
      });
  });

  return {
    promise,
    rejectWithError: (error: Error) => {
      cleanup();
      if (rejectFn) rejectFn(error);
    },
    cleanup,
  };
};

const uploadPastPaper = async (params: UploadPastPaperParams): Promise<UploadPastPaperResult> => {
  const filePath = generateFilePath(params);

  // Upload main PDF
  const { error: uploadError } = await supabaseClient.storage
    .from('past-papers')
    .upload(filePath, params.file, { upsert: false });

  if (uploadError) {
    // Check if file already exists
    if (uploadError.message?.includes('already exists') || uploadError.message?.includes('duplicate')) {
      throw new Error('A past paper with this name already exists. Please check the date and paper details.');
    }
    throw new Error(`Failed to upload PDF: ${uploadError.message}`);
  }

  console.log('PDF uploaded. Setting up Realtime subscription for:', filePath);

  // Set up Realtime subscription BEFORE calling the edge function
  const { promise: realtimePromise, rejectWithError, cleanup } = setupRealtimeWait(filePath, REALTIME_TIMEOUT_MS);

  // Fire the edge function - pass error handler to reject Realtime promise on quick failures
  console.log('Calling process-past-paper Edge Function with filePath:', filePath);
  invokeEdgeFunction('process-past-paper', { filePath }, (error) => {
    // Edge function failed quickly (validation error, etc.)
    // Reject the Realtime promise so the UI shows the error
    rejectWithError(error);
  });

  try {
    // Wait for Realtime to notify us when the past paper is created
    console.log('Waiting for Realtime notification...');
    const pastPaperId = await realtimePromise;

    console.log('✅ Past paper created with ID:', pastPaperId);

    return {
      pastPaperId,
      filename: filePath,
    };
  } catch (error) {
    cleanup();
    throw error;
  }
};

export const useUploadPastPaper = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadPastPaper,
    onSuccess: (data) => {
      console.log('✅ Mutation onSuccess triggered - upload complete!', data);
      toast.success('Past paper uploaded and processed successfully!');
      // Invalidate admin past papers query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['admin-past-papers'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to upload past paper. Please try again.');
    },
  });
};

export default useUploadPastPaper;
